import { Position } from "@xyflow/react";
import type { Pt } from "./cable-routing";
import { GEOMETRY } from "./canvas-geometry";
import { getDefinition } from "./catalog";
import { effectiveTerminals } from "./effective";
import { endpointPos } from "./store-helpers";
import type { Cable, SystemNode, TerminalSide } from "./types";

const { LANE_SPACING, JUNCTION_FAN_RADIUS, JUNCTION_FAN_SPREAD } = GEOMETRY;

export type Splay = { source?: number; target?: number };

export type JunctionFan = { source?: Pt; target?: Pt };

function sideDir(p: Position): Pt {
  switch (p) {
    case Position.Top:
      return { x: 0, y: -1 };
    case Position.Bottom:
      return { x: 0, y: 1 };
    case Position.Left:
      return { x: -1, y: 0 };
    case Position.Right:
    default:
      return { x: 1, y: 0 };
  }
}

function collectJunctionIds(nodes: SystemNode[]): Set<string> {
  const ids = new Set<string>();
  for (const n of nodes) {
    if (n.kind !== "component") continue;
    const def = getDefinition(n.definitionId, n.customDefinition);
    if (def?.renderVariant === "junction") ids.add(n.id);
  }
  return ids;
}

/** Side tangent, 90° CCW from the outward stub. Must match `splayAlongSide`
 *  in cable-tidy, which materialises these offsets. */
function sideTangent(side: TerminalSide): Pt {
  const sd = sideDir(
    side === "left"
      ? Position.Left
      : side === "right"
        ? Position.Right
        : side === "top"
          ? Position.Top
          : Position.Bottom,
  );
  return { x: -sd.y, y: sd.x };
}

type FanMember = {
  cableId: string;
  end: keyof Splay;
  farNodeId: string;
  farTerminalId: string;
};

/** Splay offsets so cables sharing a terminal exit along the body's side
 *  instead of stacking on one point; cables absent from the result use none.
 *  Slots go in *geometric* order — far endpoint projected onto the shared
 *  side's tangent — or two cables cross at the terminal; cable id breaks ties.
 *  Junction endpoints are excluded; `planJunctionFanOut` handles those. */
export function planTerminalFanOut(
  queue: Array<{ cable: Cable }>,
  nodes: SystemNode[],
): Map<string, Splay> {
  const isJunction = collectJunctionIds(nodes);

  const groups = new Map<string, { nodeId: string; terminalId: string; members: FanMember[] }>();
  const add = (nodeId: string, terminalId: string, member: FanMember) => {
    if (isJunction.has(nodeId)) return;
    const key = JSON.stringify([nodeId, terminalId]);
    const group = groups.get(key);
    if (group) group.members.push(member);
    else groups.set(key, { nodeId, terminalId, members: [member] });
  };
  for (const { cable } of queue) {
    add(cable.fromNodeId, cable.fromTerminalId, {
      cableId: cable.id,
      end: "source",
      farNodeId: cable.toNodeId,
      farTerminalId: cable.toTerminalId,
    });
    add(cable.toNodeId, cable.toTerminalId, {
      cableId: cable.id,
      end: "target",
      farNodeId: cable.fromNodeId,
      farTerminalId: cable.fromTerminalId,
    });
  }

  const nodesById = new Map(nodes.map((n) => [n.id, n]));
  /** Null when the definition or terminal is missing, which falls the group
   *  back to cable-id order. */
  const groupTangent = (nodeId: string, terminalId: string): { origin: Pt; tan: Pt } | null => {
    const node = nodesById.get(nodeId);
    if (!node || node.kind !== "component") return null;
    const def = getDefinition(node.definitionId, node.customDefinition);
    if (!def) return null;
    const term = effectiveTerminals(def, node).find((t) => t.id === terminalId);
    if (!term) return null;
    const origin = endpointPos(nodeId, terminalId, nodes);
    if (!origin) return null;
    return { origin, tan: sideTangent(term.side) };
  };

  const out = new Map<string, Splay>();
  for (const { nodeId, terminalId, members } of groups.values()) {
    if (members.length < 2) continue;
    const geo = groupTangent(nodeId, terminalId);
    const ranked = members.map((member) => {
      const far = geo && endpointPos(member.farNodeId, member.farTerminalId, nodes);
      const proj = geo && far
        ? (far.x - geo.origin.x) * geo.tan.x + (far.y - geo.origin.y) * geo.tan.y
        : 0;
      return { member, proj };
    });
    ranked.sort((a, b) => a.proj - b.proj || a.member.cableId.localeCompare(b.member.cableId));
    for (const [i, { member }] of ranked.entries()) {
      const splay = out.get(member.cableId) ?? {};
      splay[member.end] = (i - (ranked.length - 1) / 2) * LANE_SPACING;
      out.set(member.cableId, splay);
    }
  }
  return out;
}

/** Per-cable "fan anchors" for cables sharing a junction on the same cardinal.
 *  Routing runs to the anchor and the junction centre is appended after, so
 *  each cable's last segment is a diagonal at its own angle. Ordered by far-end
 *  direction, so the diagonals don't cross. */
export function planJunctionFanOut(
  queue: Array<{
    cable: Cable;
    from: { point: Pt; side: Position };
    to: { point: Pt; side: Position };
  }>,
  nodes: SystemNode[],
): Map<string, JunctionFan> {
  const isJunction = collectJunctionIds(nodes);

  type Slot = {
    cableId: string;
    end: "source" | "target";
    junctionPt: Pt;
    side: Position;
    /** A projection rather than an angle: atan2 wraps at ±π and scrambles the
     *  order of a left-cardinal group whose approaches sit on that seam. */
    tangentProj: number;
  };
  const slots: Slot[] = [];
  for (const entry of queue) {
    if (isJunction.has(entry.cable.fromNodeId)) {
      const sd = sideDir(entry.from.side);
      const tan = { x: -sd.y, y: sd.x };
      const far = entry.to.point;
      slots.push({
        cableId: entry.cable.id,
        end: "source",
        junctionPt: entry.from.point,
        side: entry.from.side,
        tangentProj:
          (far.x - entry.from.point.x) * tan.x +
          (far.y - entry.from.point.y) * tan.y,
      });
    }
    if (isJunction.has(entry.cable.toNodeId)) {
      const sd = sideDir(entry.to.side);
      const tan = { x: -sd.y, y: sd.x };
      const far = entry.from.point;
      slots.push({
        cableId: entry.cable.id,
        end: "target",
        junctionPt: entry.to.point,
        side: entry.to.side,
        tangentProj:
          (far.x - entry.to.point.x) * tan.x +
          (far.y - entry.to.point.y) * tan.y,
      });
    }
  }

  const groups = new Map<string, Slot[]>();
  for (const s of slots) {
    const key = `${s.junctionPt.x},${s.junctionPt.y},${s.side}`;
    const list = groups.get(key) ?? [];
    list.push(s);
    groups.set(key, list);
  }

  const out = new Map<string, JunctionFan>();
  for (const group of groups.values()) {
    if (group.length < 2) continue;
    group.sort(
      (a, b) =>
        a.tangentProj - b.tangentProj ||
        a.cableId.localeCompare(b.cableId),
    );
    const N = group.length;
    for (let i = 0; i < N; i++) {
      const slot = group[i];
      const lateralOffset = (i - (N - 1) / 2) * JUNCTION_FAN_SPREAD;
      const sd = sideDir(slot.side);
      const tan = { x: -sd.y, y: sd.x };
      const anchor: Pt = {
        x:
          slot.junctionPt.x +
          sd.x * JUNCTION_FAN_RADIUS +
          tan.x * lateralOffset,
        y:
          slot.junctionPt.y +
          sd.y * JUNCTION_FAN_RADIUS +
          tan.y * lateralOffset,
      };
      const cur = out.get(slot.cableId) ?? {};
      if (slot.end === "source") cur.source = anchor;
      else cur.target = anchor;
      out.set(slot.cableId, cur);
    }
  }
  return out;
}
